import axios from 'axios';
import { getApiUrl } from '../authConfig';

// ✅ Merkezi Axios instance
const apiClient = axios.create({
  baseURL: getApiUrl(),
  timeout: 30000, // 30 saniye
  headers: {
    'Content-Type': 'application/json',
  },
});

// ✅ Request Interceptor (Her istekte token ekle)
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // ✅ Sadece development'ta log
    if (process.env.NODE_ENV === 'development') {
      console.log(`📤 API Request: ${config.method.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ Request Error:', error);
    }
    return Promise.reject(error);
  }
);

// ✅ Response Interceptor (Hata yönetimi)
apiClient.interceptors.response.use(
  (response) => {
    // ✅ Dev mode'da log
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // ✅ 401 Unauthorized → Token refresh dene
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        console.log('🔄 Attempting token refresh...');

        const response = await axios.post(`${getApiUrl()}/api/Auth/RefreshToken`, {
          accessToken: localStorage.getItem('token'),
          refreshToken: refreshToken,
        });

        const { token, refreshToken: newRefreshToken } = response.data;

        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', newRefreshToken);
        localStorage.setItem('urlToken', token);

        console.log('✅ Token refreshed successfully');

        // ✅ Başarısız isteği tekrar dene
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);

      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        
        // ✅ Logout ve login'e yönlendir
        localStorage.clear();
        window.location.href = '/supplier-login';
        
        return Promise.reject(refreshError);
      }
    }

    // ✅ Diğer hatalar için detaylı log
    const errorDetails = {
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.response?.data?.message || error.message,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
    };

    console.error('❌ API Error:', errorDetails);

    // ✅ Kullanıcı dostu hata mesajı
    const userMessage = getUserFriendlyError(error);
    error.userMessage = userMessage;

    return Promise.reject(error);
  }
);

// ✅ Kullanıcı dostu hata mesajları
const getUserFriendlyError = (error) => {
  const status = error.response?.status;
  const serverMessage = error.response?.data?.message;

  if (serverMessage) return serverMessage;

  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return 'Your session has expired. Please login again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 500:
      return 'Server error. Please try again later.';
    case 503:
      return 'Service temporarily unavailable. Please try again later.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

// ✅ Export
export default apiClient;

// ✅ Helper functions
export const handleApiError = (error, customMessage) => {
  const message = error.userMessage || customMessage || 'An error occurred';
  console.error('API Error:', message, error);
  return message;
};
